import { useState, useEffect, useCallback, useRef } from "react";
import type { BlobItem } from "@azure/storage-blob";
import downloadBlob, { type BlobContent } from "@/services/storage/downloadBlob";
import mammoth from "mammoth";

// Constants for file type detection
const PDF_CONTENT_TYPE = 'application/pdf';
const DOCX_CONTENT_TYPE = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const TEXT_CONTENT_TYPES = ['text/', 'application/json', 'application/xml'];
const TEXT_FILE_EXTENSIONS = ['.md', '.txt', '.json', '.csv', '.xml', '.yaml', '.yml', '.log'];

export interface UseBlobViewerReturn {
  // State
  blobContent: BlobContent | null;
  loading: boolean;
  error: string | null;
  docxHtml: string | null;
  processingDocx: boolean;
  
  // Helper functions
  isTextFile: (contentType: string, fileName: string) => boolean;
  isPdfFile: (contentType: string) => boolean;
  isDocxFile: (contentType: string, fileName: string) => boolean;
  
  // Actions
  fetchBlobContent: () => Promise<void>;
  handleDownload: () => void;
  resetState: () => void;
}

/**
 * Custom hook for managing blob viewer state and operations
 */
export function useBlobViewer(blob: BlobItem | null, open: boolean): UseBlobViewerReturn {
  const [blobContent, setBlobContent] = useState<BlobContent | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [docxHtml, setDocxHtml] = useState<string | null>(null);
  const [processingDocx, setProcessingDocx] = useState(false);
  
  // Ref to track current blob name to prevent duplicate fetches
  const currentBlobRef = useRef<string | null>(null);

  // Helper functions for file type detection
  const isTextFile = useCallback((contentType: string, fileName: string): boolean => {
    return TEXT_CONTENT_TYPES.some(type => contentType.startsWith(type)) ||
           TEXT_FILE_EXTENSIONS.some(ext => fileName.toLowerCase().endsWith(ext));
  }, []);

  const isPdfFile = useCallback((contentType: string): boolean => {
    return contentType === PDF_CONTENT_TYPE;
  }, []);

  const isDocxFile = useCallback((contentType: string, fileName: string): boolean => {
    return contentType === DOCX_CONTENT_TYPE || fileName.toLowerCase().endsWith('.docx');
  }, []);

  // Process DOCX content using mammoth
  const processDocxContent = useCallback(async (arrayBuffer: ArrayBuffer) => {
    try {
      setProcessingDocx(true);
      setError(null);

      const result = await mammoth.convertToHtml({ arrayBuffer });
      setDocxHtml(result.value);

      // Log any warnings from mammoth
      if (result.messages.length > 0) {
        console.warn('Mammoth conversion warnings:', result.messages);
      }
    } catch (err) {
      console.error('Error processing DOCX:', err);
      setError(err instanceof Error ? err.message : 'Failed to process DOCX document');
    } finally {
      setProcessingDocx(false);
    }
  }, []);

  // Fetch blob content from server
  const fetchBlobContent = useCallback(async () => {
    if (!blob) return;
    
    // Prevent duplicate fetches for the same blob
    if (currentBlobRef.current === blob.name) return;

    try {
      setLoading(true);
      setError(null);
      currentBlobRef.current = blob.name;

      const content = await downloadBlob(blob.name);
      setBlobContent(content);
    } catch (err) {
      console.error('Error fetching blob content:', err);
      setError(err instanceof Error ? err.message : 'Failed to load file content');
      currentBlobRef.current = null;
    } finally {
      setLoading(false);
    }
  }, [blob]);

  // Handle file download
  const handleDownload = useCallback(() => {
    if (!blob || !blobContent) return;

    // Create a download link
    const link = document.createElement('a');
    if (typeof blobContent.content === 'string' && blobContent.content.startsWith('blob:')) {
      link.href = blobContent.content;
    } else {
      // Create blob from content (string or ArrayBuffer)
      const blobData = blobContent.content instanceof ArrayBuffer
        ? blobContent.content
        : new TextEncoder().encode(blobContent.content);
      const downloadBlobObj = new Blob([blobData], { type: blobContent.contentType });
      link.href = URL.createObjectURL(downloadBlobObj);
    }
    link.download = blob.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up the URL if we created it
    if (typeof blobContent.content !== 'string' || !blobContent.content.startsWith('blob:')) {
      URL.revokeObjectURL(link.href);
    }
  }, [blob, blobContent]);

  // Reset all state
  const resetState = useCallback(() => {
    setBlobContent((prevContent) => {
      // Clean up blob URLs
      if (prevContent?.content && typeof prevContent.content === 'string' && prevContent.content.startsWith('blob:')) {
        URL.revokeObjectURL(prevContent.content);
      }
      return null;
    });
    setError(null);
    setDocxHtml(null);
    setProcessingDocx(false);
    setLoading(false);
    currentBlobRef.current = null;
  }, []);

  // Clean up blob URLs when component unmounts or blob changes
  useEffect(() => {
    return () => {
      if (blobContent?.content && typeof blobContent.content === 'string' && blobContent.content.startsWith('blob:')) {
        URL.revokeObjectURL(blobContent.content);
      }
    };
  }, [blobContent]);

  // Process DOCX files when blob content is loaded
  useEffect(() => {
    if (!blobContent || !blob) return;

    if (isDocxFile(blobContent.contentType, blob.name) && blobContent.content instanceof ArrayBuffer) {
      processDocxContent(blobContent.content);
    } else {
      setDocxHtml(null);
    }
  }, [blobContent, blob, isDocxFile, processDocxContent]);

  // Fetch blob content when dialog opens and blob is set
  useEffect(() => {
    if (open && blob) {
      // Only fetch if we don't already have content for this blob
      if (currentBlobRef.current !== blob.name) {
        fetchBlobContent();
      }
    } else if (!open) {
      resetState();
    }
  }, [open, blob, fetchBlobContent]);

  return {
    // State
    blobContent,
    loading,
    error,
    docxHtml,
    processingDocx,
    
    // Helper functions
    isTextFile,
    isPdfFile,
    isDocxFile,
    
    // Actions
    fetchBlobContent,
    handleDownload,
    resetState,
  };
}
